// Appwrite connectivity check — SDK-FREE (2026-09-23).
//
// WHY THE SDK IS GONE: the Appwrite browser SDK (v27) has a global response
// handler that OVERWRITES the entire localStorage `cookieFallback` object
// whenever any response carries an X-Fallback-Cookies header:
//
//     const cookieFallback = response.headers.get('X-Fallback-Cookies');
//     if (window.localStorage && cookieFallback) {
//         window.localStorage.setItem('cookieFallback', cookieFallback);
//     }
//
// Our Worker-minted session secret is stored under that exact key
// (storeSessionSecret in api.ts). Any SDK response with that header wiped
// the secret, getJwt() then short-circuited to null (no Authorization
// header), every API call 401'd, and the user was forced to re-login —
// the "stale session every ~30 min" bug (2026-09-22, 10 accumulated
// sessions; see SCHEMA.md §18 2026-09-23 corrected postmortem).
//
// The SPA no longer uses the SDK for anything. This module keeps only the
// startup connectivity check, implemented with a plain fetch to the
// Appwrite /ping endpoint (no X-Fallback-Cookies is ever set on /ping —
// verified 2026-09-23), plus the local session type the rest of the app
// uses (previously re-exported from the SDK's Models namespace).

/** Session shape the app cares about (was Models.Session from the SDK). */
export interface AppwriteSession {
  $id: string;
  $createdAt: string;
  $updatedAt: string;
  userId: string;
  expire: string;
  provider: string;
  providerUid: string;
}

/**
 * One-shot connectivity check against Appwrite. Runs once at app startup
 * (see main.tsx). Unauthenticated plain fetch — no SDK, no cookieFallback
 * side effects, no credentials:'include' (which the SDK forced on every
 * call, sending stale a_session_* cookies that shadowed our valid secret).
 */
let pingDone = false;
export async function pingAppwriteOnce(): Promise<void> {
  if (pingDone) return;
  pingDone = true;
  const endpoint = import.meta.env.VITE_APPWRITE_ENDPOINT as string | undefined;
  const projectId = import.meta.env.VITE_APPWRITE_PROJECT_ID as string | undefined;
  if (!endpoint || !projectId) {
    console.warn("[appwrite] ping skipped — VITE_APPWRITE_ENDPOINT / VITE_APPWRITE_PROJECT_ID not set");
    return;
  }
  try {
    const res = await fetch(`${endpoint}/ping`, {
      headers: { "X-Appwrite-Project": projectId, accept: "application/json" },
      signal: AbortSignal.timeout(5_000),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    console.info("[appwrite] ping ok — endpoint reachable:", endpoint);
  } catch (e) {
    console.warn("[appwrite] ping failed —", (e as Error)?.message);
  }
}
